/* Web Push subscription helpers for the portal.
 *
 * Flow (enablePush):
 *   1. ensure the service worker is ready
 *   2. ask the browser for Notification permission (needs a user gesture)
 *   3. fetch the server's VAPID public key
 *   4. subscribe via PushManager (or reuse an existing subscription)
 *   5. POST the subscription to the API so the DeviceNotifier can reach it
 *
 * The shared axios instance already attaches Authorization + X-Account-ID, so
 * /push/* calls need no extra headers.
 */
import api from '@/store/services/api';

/** VAPID public key is base64url; PushManager wants a Uint8Array. */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** True if this browser already has an active push subscription. */
export async function isSubscribed() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  return !!(await reg.pushManager.getSubscription());
}

/**
 * Reconcile this browser's subscription with the server. Safe to call on every
 * app load; does nothing if the browser has no subscription.
 *
 * This is what heals a rotated subscription. The server upsert is keyed on the
 * endpoint and revives a row that was flagged unreachable, so re-registering
 * an endpoint the server has given up on is exactly the right move.
 *
 * It cannot resurrect a subscription the user turned OFF: disablePush() calls
 * subscription.unsubscribe(), so getSubscription() returns null afterwards and
 * there is nothing here to re-register. Off stays off.
 */
export async function syncPush() {
  if (!pushSupported()) return false;

  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return false;

    const json = subscription.toJSON();
    await api.post('/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys,
      platform: 'web',
    });
    return true;
  } catch (e) {
    // Best-effort and deliberately quiet: this runs on app load, and a failed
    // reconcile is not something to interrupt the user with.
    return false;
  }
}

export async function enablePush() {
  if (!pushSupported()) {
    throw new Error('This browser does not support push notifications.');
  }

  const reg = await navigator.serviceWorker.ready;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  const { data } = await api.get('/push/vapid-public-key');
  const key = data && data.public_key;
  if (!key) {
    throw new Error('Push is not configured on the server yet (no VAPID key).');
  }

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }

  const json = subscription.toJSON();
  await api.post('/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
    platform: 'web',
  });

  return true;
}

/**
 * Turn notifications off for this browser. The caller must have confirmed with
 * the user first — `confirmed: true` is required by the API, which refuses the
 * request without it.
 *
 * Order matters. The server is told first and the browser subscription dropped
 * second: if the API call fails we stop, leaving the browser subscribed and the
 * server row intact. Unsubscribing first would leave the server pushing at an
 * endpoint that no longer exists, which is the state this whole change exists
 * to stop creating.
 */
export async function disablePush() {
  if (!pushSupported()) return true;
  const reg = await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.getSubscription();
  if (subscription) {
    await api.post('/push/unsubscribe', {
      endpoint: subscription.endpoint,
      confirmed: true,
    });
    await subscription.unsubscribe().catch(() => {});
  }
  return true;
}
